import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopeOpen, faSpinner, faRedo } from '@fortawesome/free-solid-svg-icons';
import { toast, ToastContainer } from 'react-toastify';

import '../../../css/JuanScope/Register.css';
import '../../../css/UserAdmin/Global.css';
import 'react-toastify/dist/ReactToastify.css';

import SJDEFILogo from '../../../images/SJDEFILogo.png';
import Footer from '../Footer';

const VerifyLoginOtp = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputsRef = useRef([]);
  const [error, setError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const [otpCountdown, setOtpCountdown] = useState(180);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [isVerificationLockedOut, setIsVerificationLockedOut] = useState(false);
  const [verificationLockoutTime, setVerificationLockoutTime] = useState(0);
  const [verificationAttemptsLeft, setVerificationAttemptsLeft] = useState(3);
  const [isResendCoolingDown, setIsResendCoolingDown] = useState(false);
  const [resendCooldownTimeLeft, setResendCooldownTimeLeft] = useState(0);

  const otpTimerId = useRef(null);
  const verificationLockoutTimerId = useRef(null);
  const resendCooldownTimerId = useRef(null);

  const email = location.state?.email || '';
  const firstName = location.state?.firstName || 'User';

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!email) {
      toast.error("Session error. Please log in again to continue.");
      navigate('/admin', { replace: true });
    }
    inputsRef.current[0]?.focus();
    return () => {
      if (otpTimerId.current) clearTimeout(otpTimerId.current);
      if (verificationLockoutTimerId.current) clearTimeout(verificationLockoutTimerId.current);
      if (resendCooldownTimerId.current) clearTimeout(resendCooldownTimerId.current);
    };
  }, [email, navigate]);

  useEffect(() => {
    if (otpCountdown > 0 && !isVerificationLockedOut) {
      otpTimerId.current = setTimeout(() => setOtpCountdown(prev => prev - 1), 1000);
    } else if (otpCountdown <= 0 && !isVerificationLockedOut) {
      setCanResendOtp(true);
    }
    return () => clearTimeout(otpTimerId.current);
  }, [otpCountdown, isVerificationLockedOut]);

  useEffect(() => {
    if (isVerificationLockedOut && verificationLockoutTime > 0) {
      verificationLockoutTimerId.current = setTimeout(() => setVerificationLockoutTime(prev => prev - 1), 1000);
    } else if (isVerificationLockedOut && verificationLockoutTime <= 0) {
      setIsVerificationLockedOut(false);
      setVerificationAttemptsLeft(3);
      setCanResendOtp(true);
    }
    return () => clearTimeout(verificationLockoutTimerId.current);
  }, [isVerificationLockedOut, verificationLockoutTime]);

  useEffect(() => {
    if (isResendCoolingDown && resendCooldownTimeLeft > 0) {
        resendCooldownTimerId.current = setTimeout(() => setResendCooldownTimeLeft(prev => prev - 1), 1000);
    } else if (isResendCoolingDown && resendCooldownTimeLeft <= 0) {
      setIsResendCoolingDown(false);
      setCanResendOtp(true);
    }
    return () => clearTimeout(resendCooldownTimerId.current);
  }, [isResendCoolingDown, resendCooldownTimeLeft]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value) || isVerificationLockedOut || submitLoading || resendLoading) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (error) setError('');
    if (value && index < otp.length - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) inputsRef.current[index - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    if (isVerificationLockedOut || submitLoading || resendLoading) return;
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      setOtp(pastedData.split(''));
      inputsRef.current[otp.length - 1]?.focus();
      if (error) setError('');
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    if (otpCountdown <= 0 && !isVerificationLockedOut) {
      setError('Login OTP has expired. Please request a new code or log in again.');
      setCanResendOtp(true);
      return;
    }
    setSubmitLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/verify-login-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, loginOtp: otpString }),
      });
      const data = await response.json(); // `data` will contain user details and token on success

      if (!response.ok) {
        setError(data.message || 'Login OTP verification failed.');
        if (data.errorType === 'login_otp_lockout') {
          setIsVerificationLockedOut(true);
          setVerificationLockoutTime(data.lockoutTimeLeft || 900);
          setOtpCountdown(0);
          setCanResendOtp(false);
        }
        if (data.attemptsLeft !== undefined) setVerificationAttemptsLeft(data.attemptsLeft);
        return; // Important: Stop execution if OTP verification failed
      }

      // --- OTP Verification Successful ---
      localStorage.setItem('fullName', `${data.firstName} ${data.lastName || ''}`);
      localStorage.setItem('role', data.role || 'ROLE');
      localStorage.setItem('userID', data.userID);
      localStorage.setItem('userEmail', data.email); // Assuming 'email' is also in 'data'
      localStorage.setItem('id', data._id); // Assuming '_id' is in 'data' (MongoDB ID)
      localStorage.setItem('token', data.token);

      // --- ADDED: System Logging for Successful Login via OTP ---
      const logData = {
        userID: data.userID, // Make sure `data` from backend includes these
        accountName: `${data.firstName} ${data.lastName || ''}`,
        role: data.role || 'ROLE',
        action: 'Logged In', // You could specify 'Logged In (OTP)'
        detail: `User ${data.firstName} ${data.lastName || ''} logged in successfully after OTP verification.`,
      };

      // Asynchronously send the log, don't let it block navigation
      fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData),
      })
        .then(logRes => {
          if (!logRes.ok) {
            // Optionally log the error if log submission fails but don't block user
            logRes.json().then(errData => console.error('Failed to record login (OTP) system log - Server:', errData));
          } else {
            return logRes.json();
          }
        })
        .then(logResData => {
          if (logResData) console.log('Login (OTP) system log recorded:', logResData);
        })
        .catch(logErr => console.error('Failed to record login (OTP) system log - Network/Fetch:', logErr));
      // --- END: System Logging ---

      toast.success('Login successful! Redirecting...');
      setTimeout(() => navigate('/admin/dashboard'), 1500); // Delay for toast visibility

    } catch (err) { // Catch for network errors or if response.json() fails
      console.error("Verify Login OTP error (catch block):", err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendLoading || isResendCoolingDown || isVerificationLockedOut) return;
    setResendLoading(true);
    setError('');
    setCanResendOtp(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/resend-login-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.message || 'Failed to resend Login OTP.');
        if (data.errorType === 'login_otp_resend_cooldown') {
          setIsResendCoolingDown(true);
          setResendCooldownTimeLeft(data.cooldownTimeLeft || 60);
        } else {
          setCanResendOtp(true);
        }
        return;
      }
      toast.success(data.message || 'New Login OTP sent!');
      setOtp(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
      const newExpiry = data.otpExpiresAt ? Math.max(0, Math.floor((new Date(data.otpExpiresAt) - new Date()) / 1000)) : 180;
      setOtpCountdown(newExpiry);
      setVerificationAttemptsLeft(3);
      setIsVerificationLockedOut(false);
      setVerificationLockoutTime(0);
      setIsResendCoolingDown(true);
      setResendCooldownTimeLeft(60);
    } catch (err) {
      console.error("Resend Login OTP error (catch block):", err);
      setError('Failed to resend. Please try again.');
      setCanResendOtp(true);
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoBackToLogin = () => {
    navigate('/admin', { replace: true, state: {} });
  };

  const resendButtonDisabled = resendLoading || isVerificationLockedOut || isResendCoolingDown || !canResendOtp;
  const submitButtonDisabled = submitLoading || resendLoading || isVerificationLockedOut || (otpCountdown <= 0 && !isVerificationLockedOut);

  return (
    <div className="juan-verify-container">
      <ToastContainer position="top-center" autoClose={3000} newestOnTop />
      <header className="juan-register-header">
        <div className="juan-header-left">
          <img
            src={SJDEFILogo}
            alt="SJDEFI Logo"
            className="juan-logo-register"
          />
          <div className="juan-header-text">
            <h1>ADMIN LOGIN VERIFICATION</h1>
          </div>
        </div>
      </header>

      <div className="juan-verify-main">
        <div className="juan-verify-card">
          <div className="juan-verify-icon">
            <FontAwesomeIcon icon={faEnvelopeOpen} size="3x" />
          </div>
          <h2>Login Security Check</h2>
          <p className="juan-verify-description">
            Welcome, {firstName}! For enhanced security, please enter the 6-digit verification code sent to <strong>{email}</strong>.
          </p>

          <form onSubmit={handleVerifySubmit} className="juan-otp-form" onPaste={handleOtpPaste}>
            <div className="juan-otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputsRef.current[index] = el}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="juan-otp-input"
                  autoFocus={index === 0}
                  disabled={isVerificationLockedOut || submitLoading || resendLoading}
                />
              ))}
            </div>

            <div className="juan-otp-timer">
              {isVerificationLockedOut ? (
                <span style={{ color: '#d9534f' }}>Too many attempts. Try again in {formatTime(verificationLockoutTime)}.</span>
              ) : isResendCoolingDown ? (
                <span>Resend available in {formatTime(resendCooldownTimeLeft)}.</span>
              ) : otpCountdown > 0 ? (
                <span>Code expires in {formatTime(otpCountdown)}.</span>
              ) : (
                <span style={{ color: '#f0ad4e' }}>Code has expired. Please resend.</span>
              )}
            </div>

            {error && (
              <div className="juan-otp-error">
                {error}
                {verificationAttemptsLeft < 3 && verificationAttemptsLeft > 0 && error.toLowerCase().includes("invalid") && (
                  <div style={{ marginTop: '5px', fontSize: '0.9em' }}>({verificationAttemptsLeft} attempt(s) left before temporary lockout)</div>
                )}
              </div>
            )}

            <div className="juan-otp-actions">
              <button
                type="button"
                onClick={handleResendOtp}
                className="juan-resend-button"
                disabled={resendButtonDisabled}
              >
                {resendLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faRedo} style={{marginRight: '5px'}} /> Resend Code</>}
              </button>
              <button
                type="submit"
                className="juan-verify-button"
                disabled={submitButtonDisabled}
              >
                {submitLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Verify & Login'}
              </button>
            </div>
          </form>

          <div style={{ marginTop: '25px', textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleGoBackToLogin}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                cursor: 'pointer',
                padding: '5px',
                textDecoration: 'underline',
                fontSize: '0.9em'
              }}
              disabled={submitLoading || resendLoading}
            >
              ← Go Back to Login Page
            </button>
          </div>

          <p className="juan-verify-note" style={{marginTop: '20px'}}>
            {/* Note: This code is for completing your current login session. */}
          </p>
        </div>
      </div>

      <Footer/>
    </div>
  );
};

export default VerifyLoginOtp;